import { Lock, LockOpen, TextFields } from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/material";
import type { EditorOptions } from "@tiptap/core";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  LinkBubbleMenu,
  MenuButton,
  RichTextEditor,
  RichTextReadOnly,
  TableBubbleMenu,
  insertImages,
  type RichTextEditorRef,
} from "mui-tiptap";
import EditorMenuControls from "./EditorMenuControls";
import useExtensions from "./useExtensions";
interface PlaceholderValue {
  placeholder: string;
  label: string;
}

const normalizeDynamicPlaceholderLinks = (html: string): string => {
  if (!html) return html;

  // Keep dynamic placeholders (e.g. {{link}}) as raw href values instead of
  // auto-normalized URLs like http://{{link}} or http://%7B%7Blink%7D%7D.
  return html
    .replace(
      /href="https?:\/\/%7B%7B([^"#?]+?)%7D%7D"/gi,
      (_match, token) => `href="{{${decodeURIComponent(token)}}}"`
    )
    .replace(/href="https?:\/\/\{\{([^"#?]+?)\}\}"/gi, (_match, token) => {
      return `href="{{${token}}}"`;
    });
};

function fileListToImageFiles(fileList: FileList): File[] {
  // You may want to use a package like attr-accept
  // (https://www.npmjs.com/package/attr-accept) to restrict to certain file
  // types.
  return Array.from(fileList).filter((file) => {
    const mimeType = (file.type || "").toLowerCase();
    return mimeType.startsWith("image/");
  });
}

export default function Editor({
  id,
  placeholders,
  value = "",
  isReadOnly,
  editorConfig,
  onChange,
}: {
  id?: string;
  placeholders: PlaceholderValue[] | null;
  value?: string;
  isReadOnly?: boolean;
  editorConfig?: {
    hideFontControls?: boolean;
    showPreview?: boolean;
    showFormattingToggle?: boolean;
    showEditToggle?: boolean;
    disableEnter?: boolean;
  };
  onChange?: (content: string) => void;
}) {

  console.log("placeholders", placeholders);
  const extensions = useExtensions({
    placeholder: "Add your own content here...",
  });
  const editorContainerRef = useRef<HTMLDivElement | null>(null);
  const rteRef = useRef<RichTextEditorRef>(null);
  const [isEditable, setIsEditable] = useState(true);
  const [showMenuBar, setShowMenuBar] = useState(true);
  const [bgColor] = useState("#ffffff"); // Default white background
  const showPreview = editorConfig?.showPreview ?? true;
  const showFormattingToggle = editorConfig?.showFormattingToggle ?? true;
  const showEditToggle = editorConfig?.showEditToggle ?? true;
  const hideFontControls = editorConfig?.hideFontControls ?? false;
  const hideAllEditorControls = hideFontControls;
  const disableEnter = editorConfig?.disableEnter ?? false;

  // Keep editor content in sync when parent value changes after async loads.
  useEffect(() => {
    const editor = rteRef.current?.editor;
    if (!editor) {
      return;
    }

    const incoming = (value ?? "").trim();
    const current = editor.getHTML().trim();
    const normalize = (html: string) =>
      html === "<p></p>" || html === "<p><br></p>" ? "" : html;

    if (normalize(incoming) !== normalize(current)) {
      editor.commands.setContent(value ?? "", false);
    }
  }, [value]);

  const handleNewImageFiles = useCallback(
    (files: File[], insertPosition?: number): void => {
      if (!rteRef.current?.editor) {
        return;
      }

      // For the sake of a demo, we don't have a server to upload the files to,
      // so we'll instead convert each one to a local "temporary" object URL.
      // This will not persist properly in a production setting. You should
      // instead upload the image files to your server, or perhaps convert the
      // images to bas64 if you would like to encode the image data directly
      // into the editor content, though that can make the editor content very
      // large. You will probably want to use the same upload function here as
      // for the MenuButtonImageUpload `onUploadFiles` prop.
      const attributesForImageFiles = files.map((file) => ({
        src: URL.createObjectURL(file),
        alt: file.name,
      }));

      insertImages({
        images: attributesForImageFiles,
        editor: rteRef.current.editor,
        position: insertPosition,
      });
    },
    []
  );

  // Allow for dropping images into the editor
  const handleDrop: NonNullable<EditorOptions["editorProps"]["handleDrop"]> =
    useCallback(
      (view, event, _slice, _moved) => {
        if (!(event instanceof DragEvent) || !event.dataTransfer) {
          return false;
        }

        const imageFiles = fileListToImageFiles(event.dataTransfer.files);
        if (imageFiles.length > 0) {
          const insertPosition = view.posAtCoords({
            left: event.clientX,
            top: event.clientY,
          })?.pos;
          handleNewImageFiles(imageFiles, insertPosition);

          // Return true to treat the event as handled. We call preventDefault
          // ourselves for good measure.
          event.preventDefault();
          return true;
        }

        return false;
      },
      [handleNewImageFiles]
    );

  // Allow for pasting images
  const handlePaste: NonNullable<EditorOptions["editorProps"]["handlePaste"]> =
    useCallback(
      (_view, event, _slice) => {
        if (!event.clipboardData) {
          return false;
        }

        const pastedImageFiles = fileListToImageFiles(
          event.clipboardData.files
        );
        if (pastedImageFiles.length > 0) {
          handleNewImageFiles(pastedImageFiles);
          // Return true to mark the paste event as handled. This can for
          // instance prevent redundant copies of the same image showing up,
          // like if you right-click and copy an image from within the editor
          // (in which case it will be added to the clipboard both as a file and
          // as HTML, which Tiptap would otherwise separately parse.)
          return true;
        }

        // We return false here to allow the standard paste-handler to run.
        return false;
      },
      [handleNewImageFiles]
    );

  const [submittedContent, setSubmittedContent] = useState("");
  const [isPreviewTabOpen, setIsPreviewTabOpen] = useState(false);

  const handleInsertPlaceholder = (placeholder: string) => {
    if (!rteRef.current?.editor) return;
    rteRef.current.editor.commands.insertContent(`{{${placeholder}}}`);
  };

  return (
    <>
      {hideAllEditorControls && placeholders && placeholders.length > 0 && (
        <Box
          sx={{
            marginBottom: 1,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
            alignContent: "flex-start",
          }}
        >
          {placeholders.map((item: any) => {
            const placeholder =
              typeof item === "string" ? item : item?.placeholder;
            const label = typeof item === "string" ? item : item?.label;

            if (!placeholder) return null;

            return (
              <Button
                key={placeholder}
                variant="outlined"
                size="small"
                onClick={() => handleInsertPlaceholder(placeholder)}
                sx={{ whiteSpace: "nowrap" }}
              >
                {label || placeholder}
              </Button>
            );
          })}
        </Box>
      )}

      <Box
        ref={editorContainerRef}
        id={id}
        sx={{
          // An example of how editor styles can be overridden. In this case,
          // setting where the scroll anchors to when jumping to headings. The
          // scroll margin isn't built in since it will likely vary depending on
          // where the editor itself is rendered (e.g. if there's a sticky nav
          // bar on your site).
          "& .ProseMirror": {
            "& h1, & h2, & h3, & h4, & h5, & h6": {
              scrollMarginTop: showMenuBar ? 50 : 0,
            },
            backgroundColor: bgColor, // Apply selected background color
            padding: "12px",
          },
        }}
      >
        <RichTextEditor
          ref={rteRef}
          extensions={extensions}
          content={value}
          editable={isEditable}
          onUpdate={({ editor }) => {
            const newValue = normalizeDynamicPlaceholderLinks(editor.getHTML());
            onChange?.(newValue); // Ensure onChange is triggered
          }}
          editorProps={{
            handleDrop: handleDrop,
            handlePaste: handlePaste,
            handleKeyDown: (_view, event) => {
              if (disableEnter && event?.key === "Enter") {
                event.preventDefault();
                return true;
              }
              return false;
            },
          }}
          renderControls={() => (
            hideAllEditorControls ? null : (
              <EditorMenuControls
                editor={rteRef.current?.editor}
                placeholders={placeholders}
                hideFontControls={hideFontControls}
              />
            )
          )}
          RichTextFieldProps={{
            // The "outlined" variant is the default (shown here only as
            // example), but can be changed to "standard" to remove the outlined
            // field border from the editor
            variant: "outlined",
            MenuBarProps: {
              hide: hideAllEditorControls || !showMenuBar,
            },
            // Below is an example of adding a toggle within the outlined field
            // for showing/hiding the editor menu bar, and a "submit" button for
            // saving/viewing the HTML content
            footer: hideAllEditorControls ? null : (
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  borderTopStyle: "solid",
                  borderTopWidth: 1,
                  borderTopColor: (theme) => theme.palette.divider,
                  py: 1,
                  px: 1.5,
                }}
              >
                {showFormattingToggle && (
                  <MenuButton
                    value="formatting"
                    tooltipLabel={
                      showMenuBar ? "Hide formatting" : "Show formatting"
                    }
                    size="small"
                    onClick={() =>
                      setShowMenuBar((currentState) => !currentState)
                    }
                    selected={showMenuBar}
                    IconComponent={TextFields}
                  />
                )}

                {showEditToggle && (
                  <MenuButton
                    value="formatting"
                    tooltipLabel={
                      isEditable
                        ? "Prevent edits (use read-only mode)"
                        : "Allow edits"
                    }
                    size="small"
                    onClick={() => setIsEditable((currentState) => !currentState)}
                    selected={!isEditable}
                    IconComponent={isEditable ? Lock : LockOpen}
                  />
                )}

                {showPreview && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setIsPreviewTabOpen((prev) => {
                        if (prev) {
                          return false;
                        } else {
                          setSubmittedContent(
                            rteRef.current?.editor?.getHTML() ?? ""
                          );
                          return true;
                        }
                      });
                    }}
                  >
                    {isPreviewTabOpen ? "Hide Preview" : "Show Preview"}
                  </Button>
                )}
              </Stack>
            ),
          }}
        >
          {() =>
            hideAllEditorControls ? null : (
              <>
                <LinkBubbleMenu
                  disablePortal
                  container={editorContainerRef.current}
                />
                <TableBubbleMenu
                  disablePortal
                  container={editorContainerRef.current}
                />
              </>
            )
          }
        </RichTextEditor>
      </Box>
      {showPreview && isPreviewTabOpen && (
        <>
          <Typography variant="h5" sx={{ mt: 5 }}>
            Content Preview:
          </Typography>

          <>
            {/* <pre style={{ marginTop: 10, overflow: "auto", maxWidth: "100%" }}>
            <code>{submittedContent}</code>
          </pre> */}

            <Box mt={3} sx={{ backgroundColor: "#fff", padding: "10px" }}>
              {/* <Typography variant="overline" sx={{ mb: 2 }}>
            Content Preview:
            </Typography> */}

              <RichTextReadOnly
                content={submittedContent}
                extensions={extensions}
              />
            </Box>
          </>
        </>
      )}

    </>
  );
}
